import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Divider,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningIcon from "@mui/icons-material/Warning";

export interface CustomDialogIntroductionTourProps {
  id?: string;
  open?: boolean;
  showDivider?: boolean;
  showWarningIcon?: boolean;
  showErrorIcon?: boolean;
  header: React.ReactNode;
  footerText?: React.ReactNode;
  handleOnClose?: VoidFunction;
  showCloseIcon?: boolean;
  tertiaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonText?: string;
  dangerButtonText?: string;
  onTertiaryButtonClick?: VoidFunction;
  onSecondaryButtonClick?: VoidFunction;
  onPrimaryButtonClick?: VoidFunction;
  onDangerButtonClick?: VoidFunction;
  buttonType?: "primary" | "danger";
  iconType?: "warning" | "error";
  hideActions?: boolean;
  hideTitleBar?: boolean;
  children?: React.ReactNode;
  maxWidth?: string | number;
  maxHeight?: string | number;
  height?: string | number;
  isPrimaryButtonDisabled?: boolean;
  isDangerButtonDisabled?: boolean;
  primaryButtonFullWidth?: boolean;
  dialogActionsDivider?: boolean;
}

export default function CustomDialogIntroductionTour({
  id = "custom-dialog",
  open = true,
  showDivider = true,
  showWarningIcon = false,
  showErrorIcon = false,
  header,
  footerText,
  handleOnClose,
  showCloseIcon = true,
  iconType = "warning",
  hideTitleBar = false,
  hideActions = false,
  children,
  dialogActionsDivider = false,
}: CustomDialogIntroductionTourProps): JSX.Element {
  const isDesktop = useMediaQuery("(min-width: 1536px)");

  let IconComponent = null;
  if (iconType === "warning" && showWarningIcon) {
    IconComponent = (
      <WarningIcon
        sx={{
          color: "warning.main",
          width: "35px",
          height: "35px",
          marginRight: "16px",
        }}
      />
    );
  } else if (iconType === "error" && showErrorIcon) {
    IconComponent = (
      <ErrorOutlineOutlinedIcon
        color="error"
        sx={{
          width: "35px",
          height: "35px",
          marginRight: "16px",
        }}
      />
    );
  }

  return (
    <Dialog
      id={id}
      open={open}
      fullScreen={!isDesktop}
      PaperProps={{
        sx: {
          margin: 0,
          ...(isDesktop && {
            width: "90vw",
            height: "100vh",
            maxWidth: "none",
            maxHeight: "none",
          }),
        },
      }}
    >
      {!hideTitleBar && (
        <DialogTitle id={`${id}-title`}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              {IconComponent}
              <Typography variant="h6">{header}</Typography>
            </Stack>

            {showCloseIcon && handleOnClose && (
              <Stack justifyContent="flex-end">
                <IconButton onClick={handleOnClose} id="close-button-config">
                  <CloseOutlinedIcon />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </DialogTitle>
      )}
      {showDivider && <Divider />}
      <DialogContent sx={{ padding: 0 }} id={`${id}-content`}>
        {children}
      </DialogContent>
      {showDivider && <Divider />}
      {dialogActionsDivider && <Divider sx={{ mt: 2 }} />}
      {!hideActions && (
        <DialogActions
          sx={{
            padding: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "nowrap",
          }}
          id={`${id}-actions`}
        >
          {footerText && (
            <Stack
              sx={{
                maxWidth: "70%",
                whiteSpace: "normal",
                wordBreak: "break-word",
                marginRight: "auto",
              }}
            >
              <Typography variant="overline">{footerText}</Typography>
            </Stack>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
